import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { EmailEditor } from '@/components/email/EmailEditor';
import { TemplatePickerDialog } from '@/components/email/TemplatePickerDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Plus, Search, Mail, Clock, Users, ChevronDown, ChevronUp,
  X, GripVertical, Play, Pause, CheckCircle2, Loader2,
  Pencil, Trash2, ToggleLeft, ToggleRight, Copy, LayoutTemplate, Music,
  Languages,
} from 'lucide-react';
import { sequencesAPI, contactsAPI, aiAPI } from '@/lib/api';
import { getCurrentUser } from '@/lib/auth';
import PromoCampaignPanel from './PromoCampaignPanel';
import { useContactTypes } from '@/lib/useContactTypes';
import { useProject } from '@/lib/ProjectContext';
import { useT } from '@/lib/LanguageContext';
import { toast } from 'sonner';
import { safeFormat } from '@/lib/utils';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '@/lib/api';

// ── Constants ─────────────────────────────────────────────────────────────────
// CONTACT_TYPES is now loaded per-company via useContactTypes() hook

const VARIABLE_CHIPS = [
  { id: '{{first_name}}', label: 'First Name' },
  { id: '{{company}}', label: 'Company' },
  { id: '{{assigned_to_name}}', label: 'Assigned To' },
];

const STATUS_COLORS = {
  active: 'bg-blue-100 text-blue-700 border-blue-300',
  completed: 'bg-green-100 text-green-700 border-green-300',
  paused: 'bg-amber-100 text-amber-700 border-amber-300',
  unsubscribed: 'bg-gray-100 text-gray-600 border-gray-300',
};

const OFFICE365_HOSTS = ['smtp.office365.com', 'smtp-mail.outlook.com', 'outlook.office365.com', 'smtp.outlook.com'];

const TRANSLATE_LANGUAGES = [{ code: 'es', label: 'Spanish' }];

// ── SortableStepItem ──────────────────────────────────────────────────────────

const SortableStepItem = ({ step, index, onUpdate, onRemove, totalSteps }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `step-${index}`,
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const [subjectCursorPos, setSubjectCursorPos] = useState(0);
  const subjectRef = React.useRef(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [translating, setTranslating] = useState({});
  const aiTranslationEnabled = !!getCurrentUser()?.company?.ai_translation_enabled;

  const handleTranslate = async (langCode) => {
    if (!step.subject_template?.trim() && !step.body_template?.trim()) {
      toast.error('Write the English subject and body first');
      return;
    }
    setTranslating((prev) => ({ ...prev, [langCode]: true }));
    try {
      const res = await aiAPI.translate({
        subject: step.subject_template, body: step.body_template, target_language: langCode,
      });
      onUpdate(index, 'translations', { ...(step.translations || {}), [langCode]: res.data });
      toast.success('Translated — review and edit below');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Translation failed');
    } finally {
      setTranslating((prev) => ({ ...prev, [langCode]: false }));
    }
  };

  const updateTranslationField = (langCode, field, value) => {
    onUpdate(index, 'translations', {
      ...(step.translations || {}),
      [langCode]: { ...(step.translations?.[langCode] || {}), [field]: value },
    });
  };

  const removeTranslation = (langCode) => {
    const next = { ...(step.translations || {}) };
    delete next[langCode];
    onUpdate(index, 'translations', next);
  };

  const insertSubjectVariable = (variable) => {
    const current = step.subject_template || '';
    const newValue = current.slice(0, subjectCursorPos) + variable + current.slice(subjectCursorPos);
    onUpdate(index, 'subject_template', newValue);
    setTimeout(() => {
      if (subjectRef.current) {
        const newPos = subjectCursorPos + variable.length;
        subjectRef.current.setSelectionRange(newPos, newPos);
        subjectRef.current.focus();
      }
    }, 0);
  };

  return (
    <div ref={setNodeRef} style={style} className="border border-[#E3DDD1] rounded-lg p-4 bg-white">
      <div className="flex items-start gap-3">
        <div {...attributes} {...listeners} className="mt-2 cursor-move text-[#5C6670] hover:text-[#1E2A33]">
          <GripVertical className="w-5 h-5" />
        </div>
        <div className="flex-1 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                Step {index + 1}
              </Badge>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-[#5C6670]">Delay (days)</Label>
                <Input
                  type="number"
                  min="0"
                  value={step.delay_days}
                  onChange={(e) => onUpdate(index, 'delay_days', parseInt(e.target.value) || 0)}
                  className="w-20"
                />
              </div>
              <span className="text-sm text-[#5C6670]">
                {step.delay_days === 0 ? 'Send immediately' : `Send after ${step.delay_days} day${step.delay_days > 1 ? 's' : ''}`}
              </span>
            </div>
            {totalSteps > 1 && (
              <Button variant="ghost" size="sm" onClick={() => onRemove(index)}>
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>

          <div>
            <Label className="text-[#1E2A33]">Subject Line</Label>
            <Input
              ref={subjectRef}
              value={step.subject_template}
              onChange={(e) => { onUpdate(index, 'subject_template', e.target.value); setSubjectCursorPos(e.target.selectionStart); }}
              onSelect={(e) => setSubjectCursorPos(e.target.selectionStart)}
              onClick={(e) => setSubjectCursorPos(e.target.selectionStart)}
              placeholder="Email subject..."
              className="mt-1"
            />
            <div className="flex gap-2 mt-2">
              {VARIABLE_CHIPS.map(chip => (
                <Button key={chip.id} variant="outline" size="sm" onClick={() => insertSubjectVariable(chip.id)} className="text-xs">
                  {chip.label}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-[#1E2A33]">Email Body</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs text-[#A62B1F] border-[#E7C3BC] hover:bg-[#F7ECEA]"
                onClick={() => setShowTemplatePicker(true)}
              >
                <LayoutTemplate className="w-3.5 h-3.5 mr-1.5" />
                Choose Template
              </Button>
            </div>
            <EmailEditor
              value={step.body_template}
              onChange={(html) => onUpdate(index, 'body_template', html)}
              placeholder="Compose your email, or choose a template above…"
            />
            <TemplatePickerDialog
              open={showTemplatePicker}
              onClose={() => setShowTemplatePicker(false)}
              onSelect={(html) => onUpdate(index, 'body_template', html)}
            />
          </div>

          {aiTranslationEnabled && (
            <div className="pt-3 border-t border-[#E3DDD1]">
              <div className="flex items-center gap-1.5 mb-2">
                <Languages className="w-4 h-4 text-[#A62B1F]" />
                <Label className="text-[#1E2A33]">Translations</Label>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {TRANSLATE_LANGUAGES.map((lang) => {
                  const has = !!step.translations?.[lang.code];
                  return (
                    <Button
                      key={lang.code}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs text-[#A62B1F] border-[#E7C3BC] hover:bg-[#F7ECEA]"
                      onClick={() => handleTranslate(lang.code)}
                      disabled={translating[lang.code]}
                    >
                      {translating[lang.code]
                        ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        : <Languages className="w-3.5 h-3.5 mr-1.5" />}
                      {has ? `Re-translate to ${lang.label}` : `Translate to ${lang.label}`}
                    </Button>
                  );
                })}
              </div>

              {Object.entries(step.translations || {}).map(([code, t]) => {
                const lang = TRANSLATE_LANGUAGES.find((l) => l.code === code);
                return (
                  <div key={code} className="border border-[#E3DDD1] rounded-lg p-3 mb-2 bg-[#FAFBFC]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-[#1E2A33]">{lang?.label || code} version</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs text-red-500 hover:text-red-600"
                        onClick={() => removeTranslation(code)}
                      >
                        <X className="w-3 h-3 mr-1" /> Remove
                      </Button>
                    </div>
                    <Input
                      value={t.subject || ''}
                      onChange={(e) => updateTranslationField(code, 'subject', e.target.value)}
                      placeholder="Translated subject"
                      className="mb-2 text-sm"
                    />
                    <Textarea
                      value={t.body || ''}
                      onChange={(e) => updateTranslationField(code, 'body', e.target.value)}
                      placeholder="Translated body (HTML)"
                      rows={6}
                      className="text-sm font-mono"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── MailboxPicker: send from a connected Google or Zoho mailbox ─────────────────

const PROVIDER_NAMES = { google: 'Google', zoho: 'Zoho' };

const MailboxPicker = ({ provider, form, setForm }) => {
  const [conns, setConns] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get('/api/mail-connections')
      .then(r => setConns((r.data || []).filter(c => c.provider === provider)))
      .catch(() => setConns([]));
  }, [provider]);

  const choose = (c) => setForm(f => ({ ...f, mail_connection_id: c.id, smtp_from_email: c.email }));

  useEffect(() => {
    if (conns && conns.length && !conns.find(c => c.id === form.mail_connection_id)) choose(conns[0]);
  }, [conns]); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    try {
      setConnecting(true);
      const r = await api.get(`/api/mail-connections/oauth/${provider}/start`, { params: { return_to: '/sequences', app_origin: window.location.origin } });
      window.location.href = r.data.url;
    } catch (err) {
      setConnecting(false);
      toast.error(err?.response?.data?.detail || `Couldn't start ${PROVIDER_NAMES[provider]} sign-in`);
    }
  };

  const sendTest = async () => {
    if (!form.mail_connection_id) return;
    try {
      setTesting(true);
      const r = await api.post(`/api/mail-connections/${form.mail_connection_id}/test`, {});
      toast.success(r.data?.message || 'Test email sent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test email failed');
    } finally {
      setTesting(false);
    }
  };

  const selected = conns?.find(c => c.id === form.mail_connection_id);

  const remove = async () => {
    if (!selected) return;
    if (!window.confirm(`Remove ${selected.email} from Stagehead OS? Campaigns will no longer be able to send from it.`)) return;
    try {
      await api.delete(`/api/mail-connections/${selected.id}`);
      const rest = conns.filter(c => c.id !== selected.id);
      setConns(rest);
      if (rest.length) choose(rest[0]);
      else setForm(f => ({ ...f, mail_connection_id: '', smtp_from_email: '' }));
      toast.success(`${selected.email} removed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove the mailbox');
    }
  };

  return (
    <div className="rounded-lg border border-[#E3DDD1] bg-[#FBF8F2] px-4 py-3 mb-4">
      {conns === null ? (
        <p className="text-xs text-[#5C6670]">Loading connected mailboxes…</p>
      ) : conns.length === 0 ? (
        <>
          <p className="text-sm text-[#1E2A33] font-medium">Send from your own {PROVIDER_NAMES[provider]} mailbox</p>
          <p className="text-xs text-[#5C6670] mt-1 mb-3">
            Sign in once with {PROVIDER_NAMES[provider]} and campaigns go out from your real address. No passwords or server settings to enter.
            Connect before you start writing, as you'll leave this page to sign in.
          </p>
          <Button type="button" size="sm" onClick={connect} disabled={connecting}
            className="bg-[#A62B1F] hover:bg-[#8C2318] text-white">
            {connecting ? 'Opening…' : `Connect ${PROVIDER_NAMES[provider]} account`}
          </Button>
        </>
      ) : (
        <>
          <Label className="text-xs text-[#5C6670]">Send from</Label>
          <div className="flex gap-2 mt-1 items-center flex-wrap">
            <select
              className="flex-1 min-w-[200px] border border-[#E3DDD1] rounded-md px-3 py-2 text-sm bg-white"
              value={form.mail_connection_id || ''}
              onChange={(e) => { const c = conns.find(x => x.id === e.target.value); if (c) choose(c); }}
            >
              {conns.map(c => (
                <option key={c.id} value={c.id}>
                  {c.email}{c.status === 'needs_reconnect' ? ' (reconnect needed)' : ''}
                </option>
              ))}
            </select>
            <Button type="button" variant="outline" size="sm" onClick={sendTest} disabled={testing || !form.mail_connection_id}>
              {testing ? 'Sending…' : 'Send test to me'}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={connect} disabled={connecting}>
              + Add another
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={remove} disabled={!selected}
              className="text-[#A62B1F] hover:text-[#8C2318]" title="Remove this mailbox">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          {selected?.status === 'needs_reconnect' && (
            <p className="text-xs text-[#A62B1F] mt-2">This mailbox's sign-in expired. Click "Add another" and sign in with the same account to reconnect it.</p>
          )}
        </>
      )}
    </div>
  );
};

// ── SmtpFields (shared between New + Edit sheets) ─────────────────────────────

const SmtpFields = ({ form, setForm, ms365 = false }) => (
  <div>
    <div className="flex items-center gap-2 mb-4">
      <Mail className="w-4 h-4 text-[#A62B1F]" />
      <Label className="text-[#1E2A33] font-semibold">Sender Email Settings</Label>
    </div>

    {/* Provider toggle */}
    <div className="flex gap-2 mb-4 flex-wrap">
      {[{ id: 'google', label: 'Google / Gmail' }, { id: 'zoho', label: 'Zoho Mail' }, { id: 'microsoft365', label: 'Microsoft 365' }, { id: 'smtp', label: 'Other SMTP' }].filter(p => p.id !== 'microsoft365' || ms365 || form.provider === 'microsoft365').map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => setForm(f => ({ ...f, provider: p.id }))}
          className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
            form.provider === p.id
              ? 'bg-[#1E2A33] text-white border-[#1E2A33]'
              : 'bg-white text-[#5C6670] border-[#E3DDD1] hover:border-[#1E2A33]'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>

    {form.provider === 'microsoft365' && (
      <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 mb-4 text-xs text-blue-800">
        Sends from your organization's Microsoft 365 mailbox. Enter the mailbox address below — no password needed.
      </div>
    )}

    {(form.provider === 'google' || form.provider === 'zoho') && (
      <MailboxPicker provider={form.provider} form={form} setForm={setForm} />
    )}

    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-xs text-[#5C6670]">From Name</Label>
        <input className="mt-1 w-full border border-[#E3DDD1] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#A62B1F]/30"
          placeholder="Gerry at Stagehead" value={form.smtp_from_name}
          onChange={(e) => setForm(f => ({ ...f, smtp_from_name: e.target.value }))} />
      </div>
      {form.provider !== 'google' && form.provider !== 'zoho' && <div>
        <Label className="text-xs text-[#5C6670]">From Email *</Label>
        <input className="mt-1 w-full border border-[#E3DDD1] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#A62B1F]/30"
          type="email" placeholder="outreach@yourcompany.com" value={form.smtp_from_email}
          onChange={(e) => setForm(f => ({ ...f, smtp_from_email: e.target.value }))} />
      </div>}
    </div>

    {form.provider === 'smtp' && (
      <>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-xs text-[#5C6670]">SMTP Host *</Label>
            <input className="mt-1 w-full border border-[#E3DDD1] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#A62B1F]/30"
              placeholder="smtp.gmail.com" value={form.smtp_host}
              onChange={(e) => setForm(f => ({ ...f, smtp_host: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs text-[#5C6670]">Port</Label>
            <input className="mt-1 w-full border border-[#E3DDD1] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#A62B1F]/30"
              type="number" placeholder="587" value={form.smtp_port}
              onChange={(e) => setForm(f => ({ ...f, smtp_port: e.target.value }))} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-xs text-[#5C6670]">SMTP Username</Label>
            <input className="mt-1 w-full border border-[#E3DDD1] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#A62B1F]/30"
              placeholder="you@gmail.com" value={form.smtp_username}
              onChange={(e) => setForm(f => ({ ...f, smtp_username: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs text-[#5C6670]">{form._isEdit ? 'New Password (leave blank to keep)' : 'SMTP Password / App Password'}</Label>
            <input className="mt-1 w-full border border-[#E3DDD1] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#A62B1F]/30"
              type="password" placeholder={form._isEdit ? 'Enter only to change' : '••••••••••••'} value={form.smtp_password}
              onChange={(e) => setForm(f => ({ ...f, smtp_password: e.target.value }))} />
          </div>
        </div>
        <p className="text-xs text-[#9AA3AB] mt-2">
          For Gmail use an <strong>App Password</strong>. For other providers use port 587 (STARTTLS) or 465 (SSL).
        </p>
      </>
    )}
  </div>
);

// ── StepsBuilder (shared between New + Edit sheets) ───────────────────────────

const StepsBuilder = ({ steps, setSteps, sensors }) => {
  const addStep = () => setSteps(prev => [
    ...prev,
    { step_number: prev.length + 1, delay_days: 1, subject_template: '', body_template: '' },
  ]);

  const removeStep = (index) => setSteps(prev =>
    prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, step_number: i + 1 }))
  );

  const updateStep = (index, field, value) => setSteps(prev => {
    const next = [...prev];
    next[index] = { ...next[index], [field]: value };
    return next;
  });

  const handleDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      const oldIndex = parseInt(active.id.split('-')[1]);
      const newIndex = parseInt(over.id.split('-')[1]);
      setSteps(prev => arrayMove(prev, oldIndex, newIndex).map((s, i) => ({ ...s, step_number: i + 1 })));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Label className="text-[#1E2A33]">Email Steps</Label>
        <Button variant="outline" size="sm" onClick={addStep}>
          <Plus className="w-4 h-4 mr-2" /> Add Step
        </Button>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={steps.map((_, i) => `step-${i}`)} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {steps.map((step, index) => (
              <SortableStepItem
                key={`step-${index}`}
                step={step}
                index={index}
                onUpdate={updateStep}
                onRemove={removeStep}
                totalSteps={steps.length}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

// ── Blank form factory ────────────────────────────────────────────────────────

const blankForm = () => ({
  name: '',
  target_type: 'all',
  project_id: '',
  provider: 'google',
  smtp_host: '',
  smtp_port: 587,
  smtp_username: '',
  smtp_password: '',
  smtp_from_email: '',
  smtp_from_name: '',
  mail_connection_id: '',
  daily_limit: 50,
});

// ── Main component ────────────────────────────────────────────────────────────

export default function Sequences() {
  const { projects } = useProject();
  const location = useLocation();
  const t = useT();
  const { types: dynamicTypes } = useContactTypes();
  // Include "All Types" as the first option for selects; computed from dynamic company types
  const CONTACT_TYPES = [{ id: 'all', label: 'All Types' }, ...dynamicTypes];

  const [activeTab, setActiveTab] = useState('sequences'); // 'sequences' | 'promo'

  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSequence, setSelectedSequence] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');   // 'all' | 'active' | 'inactive'
  const [projectFilter, setProjectFilter] = useState('all'); // 'all' | project._id

  // New sequence sheet
  const [showNewSequence, setShowNewSequence] = useState(false);
  const [newForm, setNewForm] = useState(blankForm());
  // Microsoft 365 sending is only switched on for companies set up for it
  const [ms365, setMs365] = useState(false);
  // Promo Campaigns tab only shows for companies a super admin switched it on for
  const [promoOn, setPromoOn] = useState(false);
  useEffect(() => {
    api.get('/api/auth/me').then(r => {
      setPromoOn(!!r.data?.company?.promo_enabled);
      const on = !!r.data?.company?.ms365_sending;
      setMs365(on);
      if (on) setNewForm(f => (f.name || f.mail_connection_id ? f : { ...f, provider: 'microsoft365' }));
    }).catch(() => {});
  }, []);
  const [newSteps, setNewSteps] = useState([
    { step_number: 1, delay_days: 0, subject_template: '', body_template: '' },
  ]);

  // Edit sequence sheet
  const [showEditSequence, setShowEditSequence] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSteps, setEditSteps] = useState([]);

  // Enrollments
  const [enrollments, setEnrollments] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);

  // Enroll contacts dialog
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [enrollProjectFilter, setEnrollProjectFilter] = useState('');
  const [hideEnrolled, setHideEnrolled] = useState(true);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [enrolling, setEnrolling] = useState(false);

  // Test email
  const [testingEmail, setTestingEmail] = useState(false);

  // Duplicate
  const [duplicating, setDuplicating] = useState(false);

  // Enrollment delete confirm
  const [confirmDeleteEnrollment, setConfirmDeleteEnrollment] = useState(null);

  // Enrollment search
  const [enrollmentSearch, setEnrollmentSearch] = useState('');

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // ── Open new-sequence sheet from nav ──────────────────────────────────────
  useEffect(() => {
    if (location.state?.openNew) {
      setShowNewSequence(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);


  // ── Back from connecting a Google / Zoho mailbox ──────────────────────────
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const ok = qs.get('mail_connected');
    const err = qs.get('mail_error');
    if (!ok && !err) return;
    if (ok) {
      toast.success(`${ok === 'zoho' ? 'Zoho' : 'Google'} mailbox connected. Choose it under Sender Email Settings.`);
      setNewForm(f => ({ ...f, provider: ok, mail_connection_id: qs.get('connection_id') || '' }));
      setShowNewSequence(true);
    } else {
      const msg = {
        cancelled: 'Mailbox connection was cancelled.',
        access_denied: 'Mailbox connection was cancelled.',
        no_refresh_token: 'The provider did not grant ongoing access. Please try connecting again.',
        expired: 'The sign-in took too long. Please try again.',
      }[err] || 'Could not connect the mailbox. Please try again.';
      toast.error(msg);
    }
    window.history.replaceState({}, '', location.pathname);
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load sequences (all company, no project filter) ───────────────────────
  const loadSequences = async () => {
    try {
      setLoading(true);
      const res = await sequencesAPI.list();
      setSequences(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSequences(); }, []);

  // ── Load enrollments when sequence is selected ────────────────────────────
  const loadEnrollments = async (sequenceId) => {
    try {
      setLoadingEnrollments(true);
      const res = await api.get(`/api/sequences/${sequenceId}/enrollments`);
      const enriched = await Promise.all(
        (res.data || []).map(async (e) => {
          try {
            const cr = await contactsAPI.get(e.contact_id);
            return { ...e, contact: cr.data };
          } catch { return e; }
        })
      );
      setEnrollments(enriched);
    } catch {
      setEnrollments([]);
    } finally {
      setLoadingEnrollments(false);
    }
  };

  useEffect(() => {
    if (selectedSequence) loadEnrollments(selectedSequence._id);
  }, [selectedSequence?._id]);

  const loadContacts = async () => {
    try {
      const res = await contactsAPI.list({});
      setContacts(res.data || []);
    } catch { toast.error('Failed to load contacts'); }
  };

  // ── Active/Inactive toggle ────────────────────────────────────────────────
  const handleToggleActive = async (seq, e) => {
    e.stopPropagation();
    const newVal = !seq.is_active;
    // Optimistic
    setSequences(prev => prev.map(s => s._id === seq._id ? { ...s, is_active: newVal } : s));
    if (selectedSequence?._id === seq._id) {
      setSelectedSequence(s => ({ ...s, is_active: newVal }));
    }
    try {
      await sequencesAPI.update(seq._id, { is_active: newVal });
      toast.success(newVal ? 'Campaign activated' : 'Campaign paused');
    } catch {
      toast.error('Failed to update status');
      setSequences(prev => prev.map(s => s._id === seq._id ? { ...s, is_active: seq.is_active } : s));
    }
  };

  // ── Create sequence ───────────────────────────────────────────────────────
  const handleCreateSequence = async () => {
    if (!newForm.name.trim()) { toast.error('Enter a campaign name'); return; }
    if ((newForm.provider === 'google' || newForm.provider === 'zoho') && !newForm.mail_connection_id) {
      toast.error('Connect a mailbox to send from first'); return;
    }
    for (const step of newSteps) {
      if (!step.subject_template.trim() || !step.body_template.trim()) {
        toast.error('All steps must have a subject and body'); return;
      }
    }
    try {
      await sequencesAPI.create({
        ...newForm,
        project_id: newForm.project_id || null,
        steps: newSteps,
        smtp_host: newForm.provider === 'microsoft365' ? 'smtp.office365.com'
          : (newForm.provider === 'google' || newForm.provider === 'zoho') ? `${newForm.provider}.api` : newForm.smtp_host,
        smtp_port: Number(newForm.smtp_port) || 587,
        mail_connection_id: (newForm.provider === 'google' || newForm.provider === 'zoho') ? (newForm.mail_connection_id || null) : null,
        daily_limit: Number(newForm.daily_limit) || 50,
        created_by: 'current-user@heyday.com',
      });
      toast.success('Campaign created');
      setShowNewSequence(false);
      setNewForm({ ...blankForm(), provider: ms365 ? 'microsoft365' : 'google' });
      setNewSteps([{ step_number: 1, delay_days: 0, subject_template: '', body_template: '' }]);
      loadSequences();
    } catch (err) {
      console.error(err);
      toast.error('Failed to create campaign');
    }
  };

  // ── Open edit sheet ───────────────────────────────────────────────────────
  const openEditSheet = (seq) => {
    // If SMTP credentials are stored, the user explicitly chose "Other SMTP"
    // even if the host is an Office 365 address. Only use 'microsoft365' when
    // there are no credentials (Graph API / app-only flow).
    const isOffice365Host = OFFICE365_HOSTS.includes((seq.smtp_host || '').toLowerCase());
    const hasSmtpCredentials = !!(seq.smtp_username && seq.smtp_username.trim());
    const apiProvider = seq.mail_connection_id
      ? ((seq.smtp_host || '').startsWith('zoho') ? 'zoho' : 'google')
      : null;
    const provider = apiProvider || (isOffice365Host && !hasSmtpCredentials
      ? 'microsoft365'
      : (seq.smtp_host ? 'smtp' : 'microsoft365'));
    setEditForm({
      _isEdit: true,
      name: seq.name || '',
      target_type: seq.target_type || 'all',
      project_id: seq.project_id || '',
      provider,
      smtp_host: seq.smtp_host || '',
      smtp_port: seq.smtp_port || 587,
      smtp_username: seq.smtp_username || '',
      smtp_password: '',
      smtp_from_email: seq.smtp_from_email || '',
      smtp_from_name: seq.smtp_from_name || '',
      mail_connection_id: seq.mail_connection_id || '',
      daily_limit: seq.daily_limit ?? 50,
    });
    setEditSteps(seq.steps ? seq.steps.map(s => ({ ...s })) : []);
    setShowEditSequence(true);
  };

  // ── Save edit ─────────────────────────────────────────────────────────────
  const handleUpdateSequence = async () => {
    if (!editForm.name.trim()) { toast.error('Enter a campaign name'); return; }
    for (const step of editSteps) {
      if (!step.subject_template?.trim() || !step.body_template?.trim()) {
        toast.error('All steps must have a subject and body'); return;
      }
    }
    try {
      const payload = {
        name: editForm.name,
        target_type: editForm.target_type,
        steps: editSteps,
        smtp_host: editForm.provider === 'microsoft365' ? 'smtp.office365.com'
          : (editForm.provider === 'google' || editForm.provider === 'zoho') ? `${editForm.provider}.api` : editForm.smtp_host,
        smtp_port: Number(editForm.smtp_port) || 587,
        smtp_username: (editForm.provider === 'smtp') ? editForm.smtp_username : '',
        mail_connection_id: (editForm.provider === 'google' || editForm.provider === 'zoho') ? (editForm.mail_connection_id || '') : '',
        smtp_from_email: editForm.smtp_from_email,
        smtp_from_name: editForm.smtp_from_name,
        daily_limit: Number(editForm.daily_limit) || 50,
      };
      if (editForm.smtp_password) payload.smtp_password = editForm.smtp_password;
      const res = await sequencesAPI.update(selectedSequence._id, payload);
      toast.success('Campaign updated');
      setShowEditSequence(false);
      await loadSequences();
      setSelectedSequence(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update campaign');
    }
  };

  // ── Enroll contacts ───────────────────────────────────────────────────────
  const handleEnrollContacts = async () => {
    if (!selectedContactIds.length) { toast.error('Select at least one contact'); return; }
    try {
      setEnrolling(true);
      const res = await sequencesAPI.enroll(selectedSequence._id, selectedContactIds);
      const enrolled = res?.data?.enrolled ?? selectedContactIds.length;
      const skipped = res?.data?.skipped_unsubscribed || 0;
      toast.success(`Enrolled ${enrolled} contact(s)` + (skipped ? ` · ${skipped} skipped (unsubscribed)` : ''));
      setShowEnrollDialog(false);
      setSelectedContactIds([]);
      setContactSearch('');
      loadEnrollments(selectedSequence._id);
    } catch { toast.error('Failed to enroll contacts'); }
    finally { setEnrolling(false); }
  };

  const handleUpdateEnrollmentStatus = async (enrollmentId, newStatus) => {
    try {
      await api.patch(`/api/sequences/enrollments/${enrollmentId}`, { status: newStatus });
      toast.success(`Enrollment ${newStatus}`);
      loadEnrollments(selectedSequence._id);
    } catch { toast.error('Failed to update enrollment'); }
  };

  const handleTestEmail = async (sequenceId) => {
    setTestingEmail(true);
    try {
      await sequencesAPI.testEmail(sequenceId);
      toast.success('Test email sent! Check your inbox.');
    } catch (err) {
      toast.error('Test failed', { description: err?.response?.data?.detail || err.message });
    } finally { setTestingEmail(false); }
  };

  const handleDuplicate = async (seq) => {
    setDuplicating(true);
    try {
      const res = await api.post(`/api/sequences/${seq._id}/duplicate`);
      toast.success(`"${res.data.name}" created — paused by default`);
      await loadSequences();
      setSelectedSequence(res.data);
    } catch (err) {
      toast.error('Failed to duplicate campaign');
    } finally { setDuplicating(false); }
  };

  const handleDeleteEnrollment = async (enrollmentId) => {
    try {
      await api.delete(`/api/sequences/${selectedSequence._id}/enrollments/${enrollmentId}`);
      toast.success('Contact removed from campaign');
      setConfirmDeleteEnrollment(null);
      loadEnrollments(selectedSequence._id);
    } catch { toast.error('Failed to remove contact'); }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getTargetLabel = (type) => CONTACT_TYPES.find(t => t.id === type)?.label || type;

  const getProject = (projectId) => projects.find(p => p._id === projectId);

  // ── Filtered list ─────────────────────────────────────────────────────────
  const filteredSequences = sequences.filter(seq => {
    const matchSearch = !searchQuery ||
      seq.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && seq.is_active !== false) ||
      (statusFilter === 'inactive' && seq.is_active === false);
    const matchProject = projectFilter === 'all'
      || (projectFilter === '__none__' ? !seq.project_id : seq.project_id === projectFilter);
    return matchSearch && matchStatus && matchProject;
  });

  const enrolledContactIds = new Set(enrollments.map(e => e.contact_id));
  const filteredContacts = contacts.filter(c => {
    const matchSearch = !contactSearch ||
      c.full_name?.toLowerCase().includes(contactSearch.toLowerCase()) ||
      c.company?.toLowerCase().includes(contactSearch.toLowerCase()) ||
      c.email?.toLowerCase().includes(contactSearch.toLowerCase());
    const matchProject = !enrollProjectFilter || c.project_id === enrollProjectFilter || (c.project_ids || []).includes(enrollProjectFilter);
    const matchEnrolled = !hideEnrolled || !enrolledContactIds.has(c._id);
    return matchSearch && matchProject && matchEnrolled;
  });

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout title={t('sequences.title')}>
      {/* Tab switcher */}
      {promoOn && <div className="flex gap-1 mb-5 border-b border-[#E3DDD1]">
        {[
          { id: 'sequences', icon: Mail, label: 'Email Campaigns' },
          { id: 'promo', icon: Music, label: 'Promo Campaigns' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-[#A62B1F] text-[#1E2A33]'
                : 'border-transparent text-[#5C6670] hover:text-[#1E2A33] hover:border-[#E3DDD1]'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>}

      {promoOn && activeTab === 'promo' && <PromoCampaignPanel />}
      {activeTab === 'sequences' && <div className="flex h-[calc(100vh-165px)] gap-6">

        {/* ── Left panel: list ──────────────────────────────────────────── */}
        <div className="w-[380px] flex flex-col gap-3">

          {/* New sequence button */}
          <Button
            style={{ backgroundColor: '#A62B1F', fontFamily: 'Manrope' }}
            onClick={() => setShowNewSequence(true)}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-2" /> {t('sequences.new_campaign')}
          </Button>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6670]" />
            <Input
              placeholder={t('sequences.search')}
              className="pl-9 text-sm"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Status filter pills */}
          <div className="flex gap-1.5">
            {[
              { id: 'all', label: 'All' },
              { id: 'active', label: 'Active' },
              { id: 'inactive', label: 'Paused' },
            ].map(f => (
              <button
                key={f.id}
                type="button"
                onClick={() => setStatusFilter(f.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  statusFilter === f.id
                    ? 'bg-[#1E2A33] text-white border-[#1E2A33]'
                    : 'bg-white text-[#5C6670] border-[#E3DDD1] hover:border-[#1E2A33]'
                }`}
              >
                {f.label}
              </button>
            ))}
            <div className="ml-auto">
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="h-7 text-xs border-[#E3DDD1] w-36">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  <SelectItem value="__none__">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full inline-block border border-dashed border-[#9AA3AB]" />
                      No project
                    </span>
                  </SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p._id} value={p._id}>
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.color }} />
                        {p.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Sequence cards */}
          <ScrollArea className="flex-1">
            <div className="space-y-2 pr-1">
              {loading ? (
                <p className="text-center text-[#5C6670] py-8 text-sm">{t('sequences.loading')}</p>
              ) : filteredSequences.length === 0 ? (
                <p className="text-center text-[#5C6670] py-8 text-sm">
                  {sequences.length === 0 ? t('sequences.none') : 'No matches'}
                </p>
              ) : filteredSequences.map(seq => {
                const project = getProject(seq.project_id);
                const isActive = seq.is_active !== false;
                return (
                  <Card
                    key={seq._id}
                    className={`cursor-pointer transition-all ${
                      selectedSequence?._id === seq._id
                        ? 'border-[#A62B1F] bg-[#FFF5F4]'
                        : 'hover:border-[#CBD5E0] hover:shadow-sm'
                    }`}
                    onClick={() => setSelectedSequence(seq)}
                  >
                    <CardContent className="pt-4 pb-3 space-y-2">
                      {/* Name row */}
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-[#1E2A33] text-sm leading-snug" style={{ fontFamily: 'Manrope' }}>
                          {seq.name}
                        </h3>
                        {/* Active toggle */}
                        <button
                          type="button"
                          onClick={(e) => handleToggleActive(seq, e)}
                          title={isActive ? 'Click to pause' : 'Click to activate'}
                          className="flex-shrink-0 mt-0.5"
                        >
                          {isActive
                            ? <ToggleRight className="w-5 h-5 text-green-500" />
                            : <ToggleLeft className="w-5 h-5 text-[#9AA3AB]" />
                          }
                        </button>
                      </div>

                      {/* Project badge + meta */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {project ? (
                          <span
                            className="text-xs px-2 py-0.5 rounded-full font-medium text-white"
                            style={{ backgroundColor: project.color || '#A62B1F' }}
                          >
                            {project.name}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium text-[#5C6670] border border-dashed border-[#CBD5E0]">
                            Company-wide
                          </span>
                        )}
                        <Badge variant="outline" className="text-xs">
                          {getTargetLabel(seq.target_type)}
                        </Badge>
                        <span className={`text-xs font-medium ${isActive ? 'text-green-600' : 'text-[#9AA3AB]'}`}>
                          {isActive ? t('sequences.active') : t('sequences.paused')}
                        </span>
                      </div>

                      {/* Stats row */}
                      <div className="flex items-center gap-3 text-xs text-[#5C6670]">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />{seq.steps?.length || 0} steps
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />{seq.enrolled_count || 0} enrolled
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />{seq.daily_limit ?? 50}/day
                        </span>
                        {seq.smtp_from_email ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                            {seq.smtp_from_email}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-amber-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                            No sender
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        {/* ── Right panel: detail ───────────────────────────────────────── */}
        <div className="flex-1 overflow-hidden">
          {!selectedSequence ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center space-y-3">
                <Mail className="w-16 h-16 text-[#5C6670] mx-auto opacity-30" />
                <p className="text-[#5C6670]" style={{ fontFamily: 'Manrope' }}>
                  Select a campaign to view details
                </p>
              </div>
            </div>
          ) : (
            <Card className="h-full flex flex-col">
              <CardHeader className="border-b border-[#E3DDD1]">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <CardTitle className="text-xl text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                        {selectedSequence.name}
                      </CardTitle>
                      {/* Active toggle in detail header */}
                      <button
                        type="button"
                        onClick={(e) => handleToggleActive(selectedSequence, e)}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-full border text-xs font-medium transition-colors"
                        style={{
                          borderColor: selectedSequence.is_active !== false ? '#10B981' : '#CBD5E0',
                          color: selectedSequence.is_active !== false ? '#10B981' : '#9AA3AB',
                          backgroundColor: selectedSequence.is_active !== false ? '#F0FDF4' : '#FBF8F2',
                        }}
                      >
                        {selectedSequence.is_active !== false
                          ? <><ToggleRight className="w-3.5 h-3.5" /> {t('sequences.active')}</>
                          : <><ToggleLeft className="w-3.5 h-3.5" /> {t('sequences.paused')}</>
                        }
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      {getProject(selectedSequence.project_id) && (
                        <span
                          className="text-xs px-2 py-0.5 rounded-full font-medium text-white"
                          style={{ backgroundColor: getProject(selectedSequence.project_id)?.color || '#A62B1F' }}
                        >
                          {getProject(selectedSequence.project_id)?.name}
                        </span>
                      )}
                      <Badge variant="outline">{getTargetLabel(selectedSequence.target_type)}</Badge>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" onClick={() => openEditSheet(selectedSequence)} style={{ fontFamily: 'Manrope' }}>
                      <Pencil className="w-4 h-4 mr-2" /> {t('common.edit')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDuplicate(selectedSequence)}
                      disabled={duplicating}
                      title="Duplicate this campaign (copies steps, not enrolled contacts)"
                      style={{ fontFamily: 'Manrope' }}
                    >
                      {duplicating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />}
                      Duplicate
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleTestEmail(selectedSequence._id)}
                      disabled={testingEmail || !selectedSequence.smtp_from_email}
                      title={!selectedSequence.smtp_from_email ? 'Configure sender email first' : 'Send test email'}
                      style={{ fontFamily: 'Manrope' }}
                    >
                      {testingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      Test Email
                    </Button>
                    <Button
                      style={{ backgroundColor: '#A62B1F', fontFamily: 'Manrope' }}
                      onClick={() => { loadContacts(); setShowEnrollDialog(true); }}
                    >
                      <Users className="w-4 h-4 mr-2" /> {t('sequences.enroll_contacts')}
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="flex-1 overflow-hidden pt-6">
                <Tabs defaultValue="steps" className="h-full flex flex-col">
                  <TabsList>
                    <TabsTrigger value="steps">{t('sequences.steps')}</TabsTrigger>
                    <TabsTrigger value="enrollments">{t('sequences.enrolled')} ({enrollments.length})</TabsTrigger>
                  </TabsList>

                  {/* Steps tab */}
                  <TabsContent value="steps" className="flex-1 overflow-auto mt-4">
                    <div className="space-y-4">
                      {selectedSequence.steps?.map((step, index) => (
                        <Card key={index}>
                          <CardContent className="pt-4">
                            <div className="flex items-center gap-3 mb-3">
                              <Badge variant="secondary" className="text-[#1E2A33]">Step {step.step_number}</Badge>
                              <div className="flex items-center gap-2 text-sm text-[#5C6670]">
                                <Clock className="w-4 h-4" />
                                {step.delay_days === 0 ? 'Send immediately' : `Send after ${step.delay_days} day${step.delay_days > 1 ? 's' : ''}`}
                              </div>
                            </div>
                            <div className="space-y-3">
                              <div>
                                <Label className="text-xs text-[#5C6670]">Subject</Label>
                                <p className="text-sm text-[#1E2A33] font-medium mt-0.5">{step.subject_template}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-[#5C6670]">Body</Label>
                                <div
                                  className="tiptap-preview text-sm text-[#1E2A33] border rounded p-3 bg-white mt-0.5"
                                  dangerouslySetInnerHTML={{ __html: step.body_template }}
                                />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </TabsContent>

                  {/* Enrollments tab */}
                  <TabsContent value="enrollments" className="flex-1 overflow-auto mt-4">
                    {loadingEnrollments ? (
                      <p className="text-center text-[#5C6670] py-8">{t('sequences.loading')}</p>
                    ) : enrollments.length === 0 ? (
                      <p className="text-center text-[#5C6670] py-8">{t('sequences.none')}</p>
                    ) : (
                      <div className="space-y-3">
                        {/* Enrollment search */}
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6670]" />
                          <Input
                            placeholder="Search enrolled contacts..."
                            className="pl-9 text-sm"
                            value={enrollmentSearch}
                            onChange={e => setEnrollmentSearch(e.target.value)}
                          />
                        </div>
                        {enrollments
                          .filter(e => {
                            if (!enrollmentSearch) return true;
                            const q = enrollmentSearch.toLowerCase();
                            return (
                              e.contact?.full_name?.toLowerCase().includes(q) ||
                              e.contact?.company?.toLowerCase().includes(q) ||
                              e.contact?.email?.toLowerCase().includes(q)
                            );
                          })
                          .map(enrollment => (
                          <Card key={enrollment._id}>
                            <CardContent className="pt-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="font-semibold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                                    {enrollment.contact?.full_name || 'Unknown Contact'}
                                  </h4>
                                  <p className="text-sm text-[#5C6670]">{enrollment.contact?.company || 'No company'}</p>
                                  <p className="text-xs text-[#9AA3AB]">{enrollment.contact?.email || ''}</p>
                                  <div className="flex items-center gap-4 mt-2 text-sm text-[#5C6670]">
                                    <span>Step {enrollment.current_step + 1} of {selectedSequence.steps?.length}</span>
                                    {enrollment.next_send_at && enrollment.status === 'active' && (
                                      <span>Next: {safeFormat(enrollment.next_send_at, 'MMM dd, yyyy')}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Badge variant="outline" className={STATUS_COLORS[enrollment.status]}>{enrollment.status}</Badge>
                                  {enrollment.status === 'active' && (
                                    <Button variant="ghost" size="sm" title="Pause" onClick={() => handleUpdateEnrollmentStatus(enrollment._id, 'paused')}>
                                      <Pause className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {enrollment.status === 'paused' && (
                                    <Button variant="ghost" size="sm" title="Resume" onClick={() => handleUpdateEnrollmentStatus(enrollment._id, 'active')}>
                                      <Play className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {/* Delete / confirm inline */}
                                  {confirmDeleteEnrollment === enrollment._id ? (
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost" size="sm"
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs px-2"
                                        onClick={() => handleDeleteEnrollment(enrollment._id)}
                                      >
                                        Remove
                                      </Button>
                                      <Button variant="ghost" size="sm" className="text-xs px-2"
                                        onClick={() => setConfirmDeleteEnrollment(null)}>
                                        Cancel
                                      </Button>
                                    </div>
                                  ) : (
                                    <Button
                                      variant="ghost" size="sm"
                                      title="Remove from campaign"
                                      className="text-[#9AA3AB] hover:text-red-500 hover:bg-red-50"
                                      onClick={() => setConfirmDeleteEnrollment(enrollment._id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>
      </div>}

      {/* ── New Sequence Sheet ──────────────────────────────────────────────── */}
      <Sheet open={showNewSequence} onOpenChange={setShowNewSequence}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>{t('sequences.create')}</SheetTitle>
            <SheetDescription className="text-[#5C6670]">Build an automated email campaign to nurture your contacts</SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Name */}
            <div>
              <Label className="text-[#1E2A33]">Campaign Name *</Label>
              <Input value={newForm.name} onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g., Insurance Provider Outreach" className="mt-1" />
            </div>

            {/* Project */}
            <div>
              <Label className="text-[#1E2A33]">Project</Label>
              <Select value={newForm.project_id || '__none__'} onValueChange={v => setNewForm(f => ({ ...f, project_id: v === '__none__' ? '' : v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">
                    <span className="flex items-center gap-2 text-[#5C6670]">
                      <span className="w-2.5 h-2.5 rounded-full inline-block border border-dashed border-[#9AA3AB]" />
                      No project (company-wide)
                    </span>
                  </SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p._id} value={p._id}>
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: p.color || '#A62B1F' }} />
                        {p.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Target type */}
            <div>
              <Label className="text-[#1E2A33]">Target Type</Label>
              <Select value={newForm.target_type} onValueChange={v => setNewForm(f => ({ ...f, target_type: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONTACT_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Daily send limit */}
            <div>
              <Label className="text-[#1E2A33]">Daily Email Limit</Label>
              <p className="text-xs text-[#5C6670] mt-0.5">Max emails sent per day to avoid spam filters. Contacts beyond this limit are automatically scheduled for the next day.</p>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  min="1"
                  max="500"
                  value={newForm.daily_limit}
                  onChange={e => setNewForm(f => ({ ...f, daily_limit: parseInt(e.target.value) || 50 }))}
                  className="w-28"
                />
                <span className="text-sm text-[#5C6670]">emails / day</span>
              </div>
            </div>

            <Separator />

            <StepsBuilder steps={newSteps} setSteps={setNewSteps} sensors={sensors} />

            <Separator />

            <SmtpFields form={newForm} setForm={setNewForm} ms365={ms365} />

            <div className="flex gap-2 pt-4">
              <Button style={{ backgroundColor: '#A62B1F', fontFamily: 'Manrope' }} onClick={handleCreateSequence}>
                <CheckCircle2 className="w-4 h-4 mr-2" /> {t('sequences.create')}
              </Button>
              <Button variant="outline" onClick={() => { setShowNewSequence(false); setNewForm({ ...blankForm(), provider: ms365 ? 'microsoft365' : 'google' }); setNewSteps([{ step_number: 1, delay_days: 0, subject_template: '', body_template: '' }]); }}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Edit Sequence Sheet ─────────────────────────────────────────────── */}
      <Sheet open={showEditSequence} onOpenChange={setShowEditSequence}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>{t('sequences.edit')}</SheetTitle>
            <SheetDescription className="text-[#5C6670]">Update steps, sender settings, or name</SheetDescription>
          </SheetHeader>

          {editForm && (
            <div className="mt-6 space-y-6">
              <div>
                <Label className="text-[#1E2A33]">Campaign Name *</Label>
                <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., Insurance Provider Outreach" className="mt-1" />
              </div>

              <div>
                <Label className="text-[#1E2A33]">Target Type</Label>
                <Select value={editForm.target_type} onValueChange={v => setEditForm(f => ({ ...f, target_type: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONTACT_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Daily send limit */}
              <div>
                <Label className="text-[#1E2A33]">Daily Email Limit</Label>
                <p className="text-xs text-[#5C6670] mt-0.5">Max emails sent per day to avoid spam filters.</p>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    min="1"
                    max="500"
                    value={editForm.daily_limit}
                    onChange={e => setEditForm(f => ({ ...f, daily_limit: parseInt(e.target.value) || 50 }))}
                    className="w-28"
                  />
                  <span className="text-sm text-[#5C6670]">emails / day</span>
                </div>
              </div>

              <Separator />

              <StepsBuilder steps={editSteps} setSteps={setEditSteps} sensors={sensors} />

              <Separator />

              <SmtpFields form={editForm} setForm={setEditForm} ms365={ms365} />

              <div className="flex gap-2 pt-4">
                <Button style={{ backgroundColor: '#A62B1F', fontFamily: 'Manrope' }} onClick={handleUpdateSequence}>
                  <CheckCircle2 className="w-4 h-4 mr-2" /> {t('common.save')}
                </Button>
                <Button variant="outline" onClick={() => setShowEditSequence(false)}>{t('common.cancel')}</Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ── Enroll Contacts Dialog ──────────────────────────────────────────── */}
      <Dialog open={showEnrollDialog} onOpenChange={(open) => {
        setShowEnrollDialog(open);
        if (!open) { setSelectedContactIds([]); setContactSearch(''); setEnrollProjectFilter(''); setHideEnrolled(true); }
      }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
              {t('sequences.enroll_contacts')}
            </DialogTitle>
            <DialogDescription className="text-[#5C6670]">
              Select contacts to enroll in the campaign "{selectedSequence?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6670]" />
              <Input placeholder="Search contacts..." className="pl-9" value={contactSearch}
                onChange={e => setContactSearch(e.target.value)} />
            </div>

            {/* Project filter pills */}
            {projects.length > 1 && (
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setEnrollProjectFilter('')}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    !enrollProjectFilter
                      ? 'bg-[#1E2A33] text-white'
                      : 'bg-muted text-[#5C6670] hover:bg-muted/80'
                  }`}
                >
                  All Projects
                </button>
                {projects.map(p => (
                  <button
                    key={p._id}
                    onClick={() => setEnrollProjectFilter(prev => prev === p._id ? '' : p._id)}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      enrollProjectFilter === p._id
                        ? 'text-white'
                        : 'bg-muted text-[#5C6670] hover:bg-muted/80'
                    }`}
                    style={enrollProjectFilter === p._id ? { backgroundColor: p.color || '#A62B1F' } : {}}
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color || '#A62B1F' }} />
                    {p.name}
                  </button>
                ))}
              </div>
            )}

            {/* Hide already enrolled toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setHideEnrolled(v => !v)}
                className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  hideEnrolled
                    ? 'border-[#2F6B6B] text-[#2F6B6B] bg-[#2F6B6B]/10'
                    : 'border-[#E3DDD1] text-[#5C6670] hover:bg-muted/50'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${hideEnrolled ? 'bg-[#2F6B6B]' : 'bg-[#5C6670]'}`} />
                Hide already enrolled
              </button>
              {enrolledContactIds.size > 0 && (
                <span className="text-xs text-[#5C6670]">{enrolledContactIds.size} already enrolled</span>
              )}
            </div>

            {/* Contact list */}
            <ScrollArea className="h-[360px] border border-[#E3DDD1] rounded-lg">
              {filteredContacts.length === 0 ? (
                <p className="text-center text-[#5C6670] py-8 text-sm">No contacts found</p>
              ) : (
                <div className="divide-y divide-[#E3DDD1]">
                  {filteredContacts.map(contact => {
                    const isEnrolled = enrolledContactIds.has(contact._id);
                    const isSelected = selectedContactIds.includes(contact._id);
                    const contactProject = projects.find(p => p._id === contact.project_id);
                    return (
                      <div
                        key={contact._id}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                          isSelected ? 'bg-[#A62B1F]/8' : 'hover:bg-muted/40'
                        }`}
                        onClick={() => !isEnrolled && setSelectedContactIds(prev =>
                          prev.includes(contact._id) ? prev.filter(id => id !== contact._id) : [...prev, contact._id]
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          disabled={isEnrolled}
                          className="w-4 h-4 accent-[#A62B1F]"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-[#1E2A33] text-sm truncate">{contact.full_name}</p>
                            {isEnrolled && (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[#2F6B6B]/15 text-[#2F6B6B] flex-shrink-0">
                                Enrolled
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <p className="text-xs text-[#5C6670] truncate">{contact.company || 'No company'} · {contact.email || 'No email'}</p>
                            {contactProject && (
                              <span className="flex items-center gap-1 text-[10px] text-[#5C6670] flex-shrink-0">
                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: contactProject.color || '#A62B1F' }} />
                                {contactProject.name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>

            {/* Spread estimate */}
            {selectedContactIds.length > 0 && (() => {
              const limit = selectedSequence?.daily_limit ?? 50;
              const days = Math.ceil(selectedContactIds.length / limit);
              return (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                  <strong>{selectedContactIds.length} contacts</strong> will be sent at{' '}
                  <strong>{limit}/day</strong> — spread across{' '}
                  <strong>{days} day{days !== 1 ? 's' : ''}</strong>.
                  {days > 1 && ' Contacts beyond today\'s limit are automatically scheduled for the next available day.'}
                </div>
              );
            })()}

            <div className="flex items-center justify-between">
              <p className="text-sm text-[#5C6670]">
                {selectedContactIds.length} selected · {filteredContacts.length} shown
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setShowEnrollDialog(false); setSelectedContactIds([]); setContactSearch(''); setEnrollProjectFilter(''); setHideEnrolled(true); }}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleEnrollContacts} disabled={enrolling || !selectedContactIds.length}
                  style={{ backgroundColor: '#A62B1F', fontFamily: 'Manrope' }}>
                  {enrolling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Enroll {selectedContactIds.length > 0 ? selectedContactIds.length : ''} Contact{selectedContactIds.length !== 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
